import * as actionTypes from '../actions/actionTypes';
import { DateTime } from 'luxon';
import { nanoid } from '@reduxjs/toolkit';
import { removeByKey } from '../../utils';

const initialState = {
  orderId: nanoid(),
  selectedDiet: '',
  selectedOption: '',
  selectedCalories: '',
  selectedDietCaloriesIdPrices: [],
  selectedMeals: '',
  days: '',
  selectedSectorId: '',
  selectedDays: [],
  testOrder: false,
  saturdays: true,
  sundays: true,
  selectedProgram: '',
  filteredWeekends: [],
  sets: 1,
  selectedSideOrders: [],
  minMaxModalStatus: '',
  mealExchange: {
    id: nanoid(),
    days: {},
    activeDay: '',
    mealExchangeEditModalClickedArea: '',
    mealExchangeEditModalStatus: false,
    basicPricePerDay: {
      basicPrice: 0,
      allMeals: 0,
      meals: 0,
      minimum: 0,
      sideOrders: 0,
    },
    totalPrice: {
      totalPriceAfterDiscountWithSideOrders: 0,
      totalPriceAfterDiscount: 0,
      totalPriceBeforeDiscount: 0,
      changesOnMenuConfigurationBeforeDiscount: 0,
      sideOrdersPriceSum: 0,
    },
    selectedMealModal: {
      modalStatus: false,
      selectedMeal: {},
    },
    caloriesModal: {
      modalStatus: false,
      otherCaloriesMenuMeals: [],
      menuMealName: '',
      id: '',
      mealId: null,
      mealPrice: {},
    },
  },
};

export const orderForm = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.STORE_PRICES:
      return {
        ...state,
        selectedDietCaloriesIdPrices: action.prices,
      };
    case actionTypes.RESET_SIDE_ORDERS:
      return {
        ...state,
        selectedSideOrders: [],
      };
    case actionTypes.RESTORE_INITIAL_ORDER_FORM:
      return { ...initialState, orderId: nanoid() };
    case actionTypes.STORE_LAST_ITEM_INTO_ORDER_FORM:
      return action.item;

    case actionTypes.RESET_PREVIOUSLY_SELECTED_DIET_DETAILS:
      return {
        ...state,
        selectedDiet: '',
        selectedOption: '',
        selectedCalories: '',
        selectedMeals: '',
        selectedProgram: '',
        selectedSideOrders: [],
        sets: 1,
      };
    case actionTypes.STORE_CITY:
      return {
        ...state,
        selectedSectorId: action.sector,
        selectedCity: action.name,
      };
    case actionTypes.STORE_CATERING:
      return {
        ...state,
        catering: action.name,
      };
    case actionTypes.STORE_ORDER_FROM_PRICING:
      return {
        ...state,
        selectedDiet: action.diet,
        selectedOption: action.option,
        selectedCalories: action.calories,
        selectedMeals: action.meals,
        days: action.days,
        selectedSectorId: action.sector,
        catering: action.cateringName,
      };
    case actionTypes.UPDATE_ORDER_FORM_REDUCER:
      return {
        ...state,
        ...action.order,
      };
    case actionTypes.RESET_CALENDAR: {
      return {
        ...state,
        filteredWeekends: [],
        selectedDays: [],
      };
    }
    case actionTypes.SET_DEFAULT_WEEKENDS:
      return {
        ...state,
        saturdays: action.saturdays,
        sundays: action.sundays,
      };
    case actionTypes.SET_DEFAULT_DAYS:
      return {
        ...state,
        days: action.days,
      };
    case actionTypes.STORE_EXTRA_DAYS:
      return {
        ...state,
        selectedDays: [...state.selectedDays, ...action.days],
      };
    case actionTypes.DELETE_DAY:
      return {
        ...state,
        selectedDays: state.selectedDays.filter(day => day !== action.day),
      };
    case actionTypes.STORE_DAY:
      return {
        ...state,
        selectedDays: state.selectedDays.concat(action.day),
      };
    case actionTypes.RESET_FILTERED_DAYS:
      return {
        ...state,
        filteredWeekends: action.days,
      };
    case actionTypes.STORE_FILTERED_WEEKENDS:
      return {
        ...state,
        filteredWeekends: state.filteredWeekends.concat(action.days),
      };
    case actionTypes.TOGGLE_DAY: {
      const containsSelectedDay = state.selectedDays.includes(action.day);
      if (containsSelectedDay) {
        return {
          ...state,
          selectedDays: state.selectedDays.filter(
            selectedDay => selectedDay !== action.day
          ),
          days: state.days - 1,
        };
      }
      return {
        ...state,
        selectedDays: state.selectedDays.concat(action.day),
        days: state.days + 1,
      };
    }
    case actionTypes.SELECT_DAYS:
      return {
        ...state,
        selectedDays: action.days,
      };
    case actionTypes.TOGGLE_CHECKBOX:
      return {
        ...state,
        [action.field]: action.checked,
      };
    case actionTypes.SET_DAYS:
      return {
        ...state,
        days: action.days,
      };
    case actionTypes.TOGGLE_MEAL:
      return {
        ...state,
        selectedMeals: state.selectedMeals?.map(meal => {
          if (meal.dietMealId === action.dietMealId) {
            if (action.numberOfSelectedMeals === action.minimumNumberOfMeals) {
              return {
                ...meal,
                selected: meal.selected ? meal.selected : !meal.selected,
              };
            }
            return {
              ...meal,
              selected:
                action.numberOfSelectedMeals > action.minimumNumberOfMeals
                  ? !meal.selected
                  : meal.selected,
            };
          } else {
            return meal;
          }
        }),
      };
    case actionTypes.STORE_MEALS:
      return {
        ...state,
        selectedMeals: action.meals,
      };
    case actionTypes.SELECT: {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case actionTypes.CHANGE_STEP: {
      return {
        ...state,
        step: action.step,
      };
    }
    case actionTypes.CHANGE_MIN_MAX_MODAL_STATUS: {
      return {
        ...state,
        minMaxModalStatus: action.status,
      };
    }
    case actionTypes.MEAL_EXCHANGE_SELECT_PROGRAM:
      return {
        ...state,
        selectedProgram: action.program,
      };
    case actionTypes.MEAL_EXCHANGE_CREATE_DAY_LIST: {
      const { mealsList } = action;
      const activeDay = state.mealExchange.activeDay;
      const selectedMeals = state.selectedMeals?.map(item => ({ ...item }));
      const { dietOptionId: basicDietOptionId } = state.selectedOption;
      const { calories } = state.selectedCalories;

      const reduceToFinalObject = selectedMeals
        .sort((a, b) => a.mealPriority - b.mealPriority)
        .reduce((acc, cur) => {
          cur.id = nanoid();
          cur.calories = calories;
          cur.dietOptionId = basicDietOptionId;

          let { id, mealId } = cur;

          acc[id] = cur;

          cur.list = mealsList.filter(item => item.mealId === mealId);

          return acc;
        }, {});

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          days: {
            ...state.mealExchange.days,
            [activeDay]: {
              ...state.mealExchange.days[activeDay],
              dayPriceAdjustment: {
                meals: 0,
                sideOrders: 0,
              },
              mealsList: reduceToFinalObject,
              sideOrders: state.mealExchange.days[activeDay]?.sideOrders || [],
            },
          },
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_ADD_MENU_MEAL: {
      const activeDay = state.mealExchange.activeDay;
      const { list, mealId } = action.meal;

      const meal = {
        ...action.meal,
        list: list.filter(item => item.mealId === mealId),
      };

      const mealsList = {
        ...state.mealExchange.days[activeDay].mealsList,
        [meal.id]: meal,
      };

      const keysSorted = Object.values(mealsList).sort(function (a, b) {
        return a.mealPriority - b.mealPriority;
      });

      const sortedMealsList = keysSorted.reduce((acc, cur) => {
        acc[cur.id] = cur;

        return acc;
      }, {});

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          days: {
            ...state.mealExchange.days,
            [activeDay]: {
              ...state.mealExchange.days[activeDay],
              mealsList: sortedMealsList,
            },
          },
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_REMOVE_MENU_MEAL: {
      const activeDay = state.mealExchange.activeDay;
      const id = action.id;

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          days: {
            ...state.mealExchange.days,
            [activeDay]: {
              ...state.mealExchange.days[activeDay],
              mealsList: removeByKey(
                { ...state.mealExchange.days[activeDay].mealsList },
                id
              ),
            },
          },
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_CHANGE_ACTIVE_DAY: {
      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          activeDay: action.day,
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_CHECK_DAYS: {
      const parsedDays = state.selectedDays.map(item =>
        DateTime.fromMillis(item).toISODate()
      );

      const days = Object.keys(state.mealExchange.days || {});

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          days: days.reduce((acc, cur) => {
            if (parsedDays.includes(cur)) {
              acc[cur] = state.mealExchange.days[cur];
            }
            return acc;
          }, {}),
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_CHANGE_SELECTED_MEAL_MODAL_STATUS:
      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          selectedMealModal: {
            ...state.mealExchange.selectedMealModal,
            modalStatus: action.modalStatus,
          },
        },
      };
    case actionTypes.MEAL_EXCHANGE_CHANGE_SELECTED_MEAL:
      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          selectedMealModal: {
            ...state.mealExchange.selectedMealModal,
            selectedMeal: action.selectedMeal,
          },
        },
      };
    case actionTypes.MEAL_EXCHANGE_SELECT_MENU_MEAL: {
      const activeDay = state.mealExchange.activeDay;
      const { id, mealId, menuMealsList } = action;

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          days: {
            ...state.mealExchange.days,
            [activeDay]: {
              ...state.mealExchange.days[activeDay],
              mealsList: {
                ...state.mealExchange.days[activeDay].mealsList,
                [id]: {
                  ...state.mealExchange.days[activeDay].mealsList[id],
                  list: menuMealsList.filter(item => item.mealId === mealId),
                },
              },
            },
          },
          caloriesModal: initialState.mealExchange.caloriesModal,
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_CALORIES_MODAL_TOGGLE_STATUS: {
      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          caloriesModal: {
            ...state.mealExchange.caloriesModal,
            modalStatus: !state.mealExchange.caloriesModal.modalStatus,
          },
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_STORE_CALORIES_MODAL_INFO: {
      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          caloriesModal: {
            ...state.mealExchange.caloriesModal,
            otherCaloriesMenuMeals: action.otherCaloriesMenuMeals,
            menuMealName: action.menuMealName,
            id: action.id,
            mealId: action.mealId,
            mealPrice: action.mealPrice,
          },
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_RESET_CALORIES_MODAL: {
      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          caloriesModal: initialState.mealExchange.caloriesModal,
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_TOGGLE_MEAL: {
      const activeDay = state.mealExchange.activeDay;
      const { id, mealId, menuMealsList, toggle } = action;

      if (toggle === 'on') {
        return {
          ...state,
          mealExchange: {
            ...state.mealExchange,
            days: {
              ...state.mealExchange.days,
              [activeDay]: {
                ...state.mealExchange.days[activeDay],
                mealsList: {
                  ...state.mealExchange.days[activeDay].mealsList,
                  [id]: {
                    ...state.mealExchange.days[activeDay].mealsList[id],
                    list: menuMealsList.filter(item => item.mealId === mealId),
                    selected: true,
                  },
                },
              },
            },
          },
        };
      }

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          days: {
            ...state.mealExchange.days,
            [activeDay]: {
              ...state.mealExchange.days[activeDay],
              mealsList: {
                ...state.mealExchange.days[activeDay].mealsList,
                [id]: {
                  ...state.mealExchange.days[activeDay].mealsList[id],
                  selected: false,
                },
              },
            },
          },
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_RESET: {
      return {
        ...state,
        mealExchange: {
          ...initialState.mealExchange,
          id: nanoid(),
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_STORE_BASIC_PRICE_PER_DAY: {
      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          basicPricePerDay: action.basicPricePerDay,
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_UPDATE_DAY_PRICE_ADJUSTMENT: {
      const activeDay = state.mealExchange.activeDay;
      const field = action.field;
      const currentDayPriceAdjustment =
        state.mealExchange.days[activeDay].dayPriceAdjustment[field] || 0;
      const newDayPriceAdjustment =
        currentDayPriceAdjustment + action.adjustmentValue;

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          days: {
            ...state.mealExchange.days,
            [activeDay]: {
              ...state.mealExchange.days[activeDay],
              dayPriceAdjustment: {
                ...state.mealExchange.days[activeDay].dayPriceAdjustment,
                [field]: newDayPriceAdjustment,
              },
            },
          },
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_CHANGE_EDIT_MODAL_CLICKED_AREA: {
      const mealExchangeEditModalClickedArea =
        action.mealExchangeEditModalClickedArea;

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          mealExchangeEditModalClickedArea,
          mealExchangeEditModalStatus: !!mealExchangeEditModalClickedArea,
        },
      };
    }
    case actionTypes.MEAL_EXCHANGE_CHANGE_EDIT_MODAL_STATUS: {
      const mealExchangeEditModalStatus = action.mealExchangeEditModalStatus;

      return {
        ...state,
        mealExchange: {
          ...state.mealExchange,
          mealExchangeEditModalStatus,
        },
      };
    }
    case actionTypes.RESET_ORDER_FORM: {
      return initialState;
    }
    default:
      return state;
  }
};
